import { defineChatActions } from './actionsContext'
import { clearMessages, dropRoomMessages } from './messageRoomCache'
import { checkAuth } from './runtime'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { logger } from '@/utils/eventLogger'
import type {
  IChat,
} from '@/modules/chat-matrix-fast/types/IChat'
import {
  CHAT_LIST_TAB,
  CHAT_LIST_TYPES,
  excludeEventMatrixRooms,
  hasLastMessagePreview,
  isEventChatItem,
  isGroupLikeChatType,
  mergeBackendChatWithMatrix,
  mergeBackendChatsWithMatrix,
  mergeEventChatFields,
  mergeMatrixChatsWithEntityEventChats,
  mergeRestEventChatsWithMatrix,
  pickFresherLastMessage,
  preserveChatRuntimeFields,
  preserveEventChatMetadata,
  preserveTotalMembers,
  resolveChatIcon,
} from '@/modules/chat-matrix-fast/constants/chat'
import { ChatApiService } from '@/modules/chat-matrix-fast/services/chatApiService'
import { pollChatUntilMatrixRoom, requestStructureRepair } from '@/modules/chat-matrix-fast/services/matrixRoomPolling'
import {
  filterChatsForSidebar,
  isTopicRoomLocallyRead,
  sameChatId,
} from '@/modules/chat-matrix-fast/utils/topics'
import {
  fetchChatFromMatrix,
  fetchMatrixChats,
  fetchMatrixRoomLastMessagePreview,
  hasMatrixMessagePreview,
  clearMatrixRoomHistoryForbidden,
  isMatrixRoomHistoryForbidden,
  isChatAccessibleInMatrix,
  isSyntheticMatrixChatId,
  kickMatrixRoomMember,
  resolveMatrixRoomIdByChatId,
} from '@/modules/chat-matrix-fast/services/matrixChatClient'
import { preserveGroupChatMetadata } from '@/modules/chat-matrix-fast/services/matrixRoomMapper'
import {
  collectBackendCatalogRoomIds,
  isForgottenRoom,
  reconcileForgottenRoomsWithBackend,
  restoreForgottenRoom,
} from '@/modules/chat-matrix-fast/services/matrixForgottenRooms'
import { ensureMatrixChatMembership } from '@/modules/chat-matrix-fast/services/ensureMatrixChatMembership'
import { loadEmployeesTree } from '@/modules/chat-matrix-fast/services/employeeLookup'
import { chatsNeedEmployeeTree } from '@/modules/chat-matrix-fast/stores/core/employeeActions'
import { filterValidUserIds } from '@/modules/chat-matrix-fast/utils/validation'

import {
  getViewChatContactIds,
  notifyViewForceSortAndSync,
  notifyViewLastMessage,
  syncViewWithCore,
} from '@/modules/chat-matrix-fast/stores/chatStoreSync'
import { chatDebugLog, chatPerfLog, lastMessageDebugLog, setChatDebugLane, summarizeChatLastMessageFieldsForLog, summarizeChatTitlesHeadline, summarizeChatsForLog, summarizeDialogTitleSourcesForLog, summarizeLastMessageForLog } from '@/modules/chat-matrix-fast/utils/chatDebugLog'
import {
  describeEventChatMatrixRoom,
  logEventChat,
  logEventChats,
} from '@/modules/chat-matrix-fast/utils/eventChatDebug'
import {
  hasPendingChatListPreviews,
  markChatListPreviewPending,
  settleChatListPreviewEmpty,
  settleChatListPreviewReady,
  syncChatListPreviewPending,
} from '@/modules/chat-matrix-fast/utils/chatListPreviewState'
import {
  isPersistedChatListPreviewEmpty,
  loadPersistedChatListPreview,
  persistChatListPreview,
  persistChatListPreviewEmpty,
} from '@/modules/chat-matrix-fast/utils/chatListPreviewPersistence'
import {
  loadChatListSnapshot,
  persistChatListSnapshot,
} from '@/modules/chat-matrix-fast/utils/chatListSnapshotPersistence'

/** Dedup concurrent Matrix preview hydrations for the events sidebar. */
const eventListPreviewHydrateInFlight = new Map<string, Promise<void>>()

type EventListPreviewSnapshot = Pick<IChat, 'last_message' | 'last_message_id' | 'last_matrix_event_id'>

/** Survives silent fetchChats merge when REST returns empty last_message. */
const eventListPreviewCache = new Map<string, EventListPreviewSnapshot>()
const eventListPreviewHydratedAt = new Map<string, number>()
const EVENT_LIST_PREVIEW_HYDRATE_COOLDOWN_MS = 60_000

function cacheEventListPreview(chat: IChat): void {
  if (!hasLastMessagePreview(chat.last_message)) {
    return
  }

  const key = String(chat.id)
  const snapshot = {
    last_message: chat.last_message,
    last_message_id: chat.last_message_id ?? chat.last_message?.id,
    last_matrix_event_id: chat.last_matrix_event_id ?? chat.last_message?.matrix_event_id,
  }
  eventListPreviewCache.set(key, snapshot)
  eventListPreviewHydratedAt.set(key, Date.now())
  persistChatListPreview(
    chat.id,
    chat.last_message,
    snapshot.last_message_id,
    chat.last_matrix_event_id ?? chat.last_message?.matrix_event_id,
  )
}

function restoreEventListPreviewFromCache(chat: IChat): IChat | null {
  const key = String(chat.id)
  let cached = eventListPreviewCache.get(key)
  if (!cached?.last_message || !hasLastMessagePreview(cached.last_message)) {
    const persisted = loadPersistedChatListPreview(chat.id)
    if (persisted?.last_message && hasLastMessagePreview(persisted.last_message)) {
      cached = persisted
      eventListPreviewCache.set(key, persisted)
    }
  }

  if (!cached?.last_message || !hasLastMessagePreview(cached.last_message)) {
    return null
  }

  return {
    ...chat,
    last_message: cached.last_message,
    last_message_id: cached.last_message_id ?? cached.last_message.id,
    last_matrix_event_id: (cached as { last_matrix_event_id?: string }).last_matrix_event_id
      ?? cached.last_message.matrix_event_id,
  }
}

function shouldSkipEventListPreviewHydrate(chatId: number | string): boolean {
  const key = String(chatId)
  const hydratedAt = eventListPreviewHydratedAt.get(key) ?? 0
  // Недавно уже тянули превью (успех или пусто) — не крутим repair/join снова.
  if (Date.now() - hydratedAt < EVENT_LIST_PREVIEW_HYDRATE_COOLDOWN_MS) {
    return true
  }

  // Между перезагрузками: пустые комнаты не долбим снова 30 минут.
  return isPersistedChatListPreviewEmpty(chatId)
}

function markEventListPreviewHydrateAttempted(chatId: number | string): void {
  eventListPreviewHydratedAt.set(String(chatId), Date.now())
  persistChatListPreviewEmpty(chatId)
}

/** Dedup concurrent GET /chat/{id}/ from openChat + structure poll + URL sync. */
const fetchChatInFlight = new Map<number, Promise<IChat>>()
const fetchChatFetchedAt = new Map<number, number>()
const FETCH_CHAT_COOLDOWN_MS = 10_000

/** Чаты задач для store — нужны и на вкладке «Чаты», чтобы был unread соседнего таба. */
function mergeEventChatsForStore(
  restEventChats: IChat[],
  matrixChats: IChat[],
  previousChats: IChat[],
): IChat[] {
  const mergedFromRest = mergeRestEventChatsWithMatrix(restEventChats, matrixChats)
  const restRoomIds = new Set(
    restEventChats
      .map(chat => chat.matrix_room_id)
      .filter((roomId): roomId is string => Boolean(roomId)),
  )
  const matrixOnlyEvents = matrixChats.filter(chat =>
    isEventChatItem(chat)
    && (!chat.matrix_room_id || !restRoomIds.has(chat.matrix_room_id)),
  )

  const merged = filterChatsForSidebar(
    mergeMatrixChatsWithEntityEventChats(
      [...mergedFromRest, ...matrixOnlyEvents],
      previousChats,
    ),
  )
  logEventChatsMergeStage('merge · store', restEventChats, matrixChats, merged)
  return merged
}

function logEventChatsMergeStage(
  stage: string,
  restEventChats: IChat[],
  matrixChats: IChat[],
  result: IChat[],
): void {
  logEventChats(stage, result, {
    restCount: restEventChats.length,
    matrixEventRooms: matrixChats.filter(isEventChatItem).length,
  })
}

/** Keep the other tab's models current from the same complete business catalog. */
function employeeChatsForEventsTab(
  catalogChats: IChat[],
  matrixChats: IChat[],
  restEventChats: IChat[],
): IChat[] {
  return filterChatsForSidebar(
    mergeBackendChatsWithMatrix(
      catalogChats.filter(chat => !isEventChatItem(chat)),
      excludeEventMatrixRooms(matrixChats.filter(chat => !isEventChatItem(chat)), restEventChats),
    ),
  )
}

function hasSidebarListChanges(previous: IChat[], next: IChat[]): boolean {
  if (previous.length !== next.length) {
    return true
  }

  const prevById = new Map(previous.map(chat => [String(chat.id), chat]))
  for (const chat of next) {
    const prev = prevById.get(String(chat.id))
    if (!prev) {
      return true
    }

    if ((prev.unread_count ?? 0) !== (chat.unread_count ?? 0)) {
      return true
    }

    const prevLastId = prev.last_message?.id ?? prev.last_message_id
    const nextLastId = chat.last_message?.id ?? chat.last_message_id
    if (prevLastId !== nextLastId) {
      return true
    }

    if (prev.title !== chat.title) {
      return true
    }

    if (prev.icon !== chat.icon || prev.display_avatar_url !== chat.display_avatar_url) {
      return true
    }
  }

  return false
}

/** Подтянуть last_message из Matrix-списка, если merge/REST не дали превью. */
function applyMatrixListPreviewFallback(
  chat: IChat,
  matrixChatsByRoomId: Map<string, IChat>,
): IChat {
  const roomId = chat.matrix_room_id?.trim()
  if (!roomId || hasLastMessagePreview(chat.last_message)) {
    return chat
  }

  const matrixChat = matrixChatsByRoomId.get(roomId)
  if (!matrixChat || !hasLastMessagePreview(matrixChat.last_message)) {
    const fromCache = restoreEventListPreviewFromCache(chat)
    if (!fromCache || !hasLastMessagePreview(fromCache.last_message)) {
      return chat
    }

    return {
      ...chat,
      ...pickFresherLastMessage(chat, fromCache),
    }
  }

  return {
    ...chat,
    ...pickFresherLastMessage(chat, matrixChat),
  }
}

/**
 * Список только из REST + прошлого состояния — показываем, пока Matrix ещё делает initial sync.
 * Без фильтров доступа по Matrix (они применяются в финальном проходе fetchChats).
 */
function buildProvisionalChatList(
  backendChats: IChat[],
  restEventChats: IChat[],
  previousChats: IChat[],
): IChat[] {
  const previousByRoomId = new Map(
    previousChats
      .filter(chat => chat.matrix_room_id)
      .map(chat => [chat.matrix_room_id!, chat] as const),
  )
  const noMatrixChats = new Map<string, IChat>()
  const nextChats = [
    ...filterChatsForSidebar(mergeBackendChatsWithMatrix(backendChats, [])),
    ...mergeEventChatsForStore(restEventChats, [], previousChats),
  ]

  return nextChats.map(chat => {
    const previous = (chat.matrix_room_id ? previousByRoomId.get(chat.matrix_room_id) : undefined)
      ?? previousChats.find(item => sameChatId(item.id, chat.id))
    let merged = chat
    if (previous) {
      merged = isEventChatItem(previous) || isEventChatItem(chat)
        ? preserveEventChatMetadata(previous, chat)
        : preserveGroupChatMetadata(previous, chat)
      merged = {
        ...preserveChatRuntimeFields(previous, merged),
        ...pickFresherLastMessage(previous, merged),
      }
    }

    merged = applyMatrixListPreviewFallback(merged, noMatrixChats)
    if (merged.matrix_room_id && isTopicRoomLocallyRead(merged.matrix_room_id)) {
      merged = { ...merged, unread_count: 0 }
    }
    return merged
  })
}

export const chatListActions = defineChatActions({
  async fetchChats(types?: string, options?: { silent?: boolean }): Promise<void> {
    if (!checkAuth()) {
      return
    }

    if (types !== undefined) {
      this.currentChatListTypes = types
    }

    const requestId = ++this.chatCatalogRequestId
    const isStale = () => requestId !== this.chatCatalogRequestId
    const fb = useFeedbackStore()
    const silent = options?.silent === true
    this.chatCatalogError = null

    if (!silent) {
      fb.isGlobalChatLoading = true
      chatPerfLog('fetchChats · start', {
        types: this.currentChatListTypes,
        silent,
      })
    }

    try {
      const previousChats = [...this.chats]
      const isEventsTab = this.currentChatListTypes === CHAT_LIST_TYPES[CHAT_LIST_TAB.EVENTS]
      setChatDebugLane(isEventsTab ? 'tasks' : 'chats')
      const isBootFetch = !silent && !isEventsTab && !this.isInitialized

      if (isBootFetch && previousChats.length === 0) {
        const snapshot = loadChatListSnapshot()
        if (snapshot?.length) {
          this.chats = snapshot
          syncChatListPreviewPending(this.chats, false)
          fb.isGlobalChatLoading = false
          notifyViewForceSortAndSync()
          chatPerfLog('fetchChats · snapshot painted (localStorage)', {
            chatsCount: snapshot.length,
          })
        }
      }

      let matrixChats: IChat[] = []

      const settleOne = <T>(promise: Promise<T>) =>
        Promise.allSettled([promise]).then(([result]) => result)
      const catalogPromise = ChatApiService.fetchChatCatalog()
        .then(catalog => {
          if (!isStale()) {
            this.chatDirectories = catalog.directories
            this.rootChatDirectoryIds = catalog.root_directory_ids
            this.ungroupedChatIds = catalog.ungrouped_chat_ids
            this.chatCatalogLoaded = true
          }
          return catalog
        })
        .catch((error: unknown) => {
          if (!isStale()) {
            this.chatCatalogError = 'Не удалось обновить папки чатов'
          }
          throw error
        })
      const primaryCatalogPromise = settleOne(
        catalogPromise.then(catalog => catalog.chats.filter(chat => isEventChatItem(chat) === isEventsTab)),
      )
      const eventCatalogPromise = isEventsTab
        ? null
        : settleOne(catalogPromise.then(catalog => catalog.chats.filter(isEventChatItem)))

      if (!isEventsTab) {
        void primaryCatalogPromise.then(result => {
          if (result.status === 'fulfilled' && chatsNeedEmployeeTree(result.value)) {
            void loadEmployeesTree()
          }
        })
      }

      let matrixSettled = false
      const matrixChatsPromise = fetchMatrixChats()
        .catch((error: unknown) => {
          const matrixError = error instanceof Error ? error.message : String(error)
          logger.error('chat_fetchChats_matrix_error', {
            file: 'chatStore',
            function: 'fetchChats',
            condition: matrixError,
          })
          return [] as IChat[]
        })
        .finally(() => {
          matrixSettled = true
        })

      let provisionalPublished = false
      if (isBootFetch && eventCatalogPromise) {
        void Promise.all([primaryCatalogPromise, eventCatalogPromise]).then(([primary, events]) => {
          if (isStale() || matrixSettled || primary.status !== 'fulfilled') {
            return
          }

          const restEvents = events.status === 'fulfilled' ? events.value : []
          this.chats = this.resolveFallbackDialogTitles(
            buildProvisionalChatList(primary.value, restEvents, previousChats),
          )
          syncChatListPreviewPending(this.chats, false)
          provisionalPublished = true
          fb.isGlobalChatLoading = false
          notifyViewForceSortAndSync()
          chatPerfLog('fetchChats · provisional list ready (REST, Matrix ещё sync)', {
            chatsCount: this.chats.length,
          })
        })
      }

      matrixChats = await matrixChatsPromise
      const catalog = await catalogPromise
      if (isStale()) {
        return
      }

      const matrixChatsByRoomId = new Map<string, IChat>()
      for (const matrixChat of matrixChats) {
        if (matrixChat.matrix_room_id) {
          matrixChatsByRoomId.set(matrixChat.matrix_room_id, matrixChat)
        }
      }

      let nextChats: IChat[]

      const backendCatalogChatIds = new Set<number | string>(catalog.chats.map(chat => chat.id))
      const backendCatalogRoomIds = collectBackendCatalogRoomIds(catalog.chats)

      if (isEventsTab) {
        // НС/заявки: REST types=event + Matrix (state может ещё не быть).
        let restEventChats: IChat[] = []
        try {
          const restResult = await primaryCatalogPromise
          if (isStale()) {
            return
          }
          if (restResult.status === 'rejected') {
            throw restResult.reason
          }
          restEventChats = restResult.value
            .map(chat => mergeEventChatFields(chat))
            .filter(isEventChatItem)
          logEventChats('rest · fetched', restEventChats, {
            apiTypes: this.currentChatListTypes,
          })
          reconcileForgottenRoomsWithBackend(restEventChats)
        } catch (error) {
          logger.error('chat_fetchEventChats_error', {
            file: 'chatStore',
            function: 'fetchChats',
            condition: String(error),
          })
        }

        const eventChats = mergeEventChatsForStore(restEventChats, matrixChats, previousChats)
        nextChats = [
          ...eventChats,
          ...employeeChatsForEventsTab(catalog.chats, matrixChats, restEventChats),
        ]

        chatDebugLog('fetchChats [events tab] → rest event + matrix', {
          matrixChatsCount: matrixChats.length,
          restEventChatsCount: restEventChats.length,
          resultCount: nextChats.length,
          chats: summarizeChatsForLog(nextChats, nextChats.length),
        })
        logEventChats('fetch · events tab merged', eventChats, {
          employeesKept: nextChats.length - eventChats.length,
        })
      } else {
        // Вкладка «Чаты»: Matrix + backend; event-чаты оставляем в store для бейджа.
        let backendChats: IChat[] = []
        let restEventChats: IChat[] = []
        const [backendCatalogResult, eventCatalogResult] = await Promise.all([
          primaryCatalogPromise,
          eventCatalogPromise ?? settleOne(
            catalogPromise.then(snapshot => snapshot.chats.filter(isEventChatItem)),
          ),
        ])
        if (isStale()) {
          return
        }

        if (backendCatalogResult.status === 'fulfilled') {
          backendChats = backendCatalogResult.value
          reconcileForgottenRoomsWithBackend(backendChats)
        } else {
          logger.error('chat_fetchChats_backend_error', {
            file: 'chatStore',
            function: 'fetchChats',
            condition: String(backendCatalogResult.reason),
          })
        }

        if (eventCatalogResult.status === 'fulfilled') {
          restEventChats = eventCatalogResult.value
          reconcileForgottenRoomsWithBackend(restEventChats)
        } else {
          logger.error('chat_fetchEventChats_error', {
            file: 'chatStore',
            function: 'fetchChats',
            condition: String(eventCatalogResult.reason),
          })
        }

        // The business catalog excludes unregistered rooms. Absence here must
        // never trigger a Matrix leave/forget or change room membership.

        const matrixOnly = excludeEventMatrixRooms(
          matrixChats.filter(chat => !isEventChatItem(chat)),
          restEventChats,
        )

        nextChats = mergeBackendChatsWithMatrix(backendChats, matrixOnly)

        // Only registered business chats belong to this catalog. Listing folders
        // must not register external Matrix rooms or change their membership.
        nextChats = [
          ...filterChatsForSidebar(nextChats),
          ...mergeEventChatsForStore(restEventChats, matrixChats, previousChats),
        ]

        chatDebugLog('fetchChats [employees tab] → matrix + backend topics metadata', {
          matrixChatsCount: matrixChats.length,
          backendChatsCount: backendChats.length,
          restEventChatsCount: restEventChats.length,
          resultCount: nextChats.length,
          chats: summarizeChatsForLog(nextChats, nextChats.length),
        })
      }

      const previousByRoomId = new Map(
        previousChats
          .filter(chat => chat.matrix_room_id)
          .map(chat => [chat.matrix_room_id!, chat] as const),
      )

      const mergedChats = nextChats
        .map(chat => {
          if (!chat.matrix_room_id) {
            const previous = previousChats.find(item => sameChatId(item.id, chat.id))
            if (!previous) {
              return chat
            }

            return {
              ...preserveChatRuntimeFields(previous, chat),
              ...pickFresherLastMessage(previous, chat),
            }
          }

          const previous = previousByRoomId.get(chat.matrix_room_id)
            ?? previousChats.find(item => sameChatId(item.id, chat.id))
          let merged = chat
          if (previous) {
            merged = isEventChatItem(previous) || isEventChatItem(chat)
              ? preserveEventChatMetadata(previous, chat)
              : preserveGroupChatMetadata(previous, chat)
            merged = preserveChatRuntimeFields(previous, merged)
          }

          merged = applyMatrixListPreviewFallback(merged, matrixChatsByRoomId)

          if (!hasLastMessagePreview(previous?.last_message)) {
            return merged
          }

          const previousTime = Date.parse(previous.last_message.created_at ?? '')
          const fetchedTime = Date.parse(merged.last_message?.created_at ?? '')
          const mergedHasPreview = hasLastMessagePreview(merged.last_message)

          if (!mergedHasPreview || (!Number.isNaN(previousTime) && previousTime >= fetchedTime)) {
            const matrixChat = matrixChatsByRoomId.get(chat.matrix_room_id)

            if (isEventChatItem(chat)) {
              logEventChat('fetch · preserve last_message', merged, {
                silent,
                reason: !mergedHasPreview ? 'merged-empty' : 'previous-fresher-or-equal',
                previous: summarizeLastMessageForLog(previous.last_message),
                matrix: summarizeLastMessageForLog(matrixChat?.last_message),
                previousTime,
                fetchedTime,
              })
            } else {
              lastMessageDebugLog('fetchChats · preserve previous last_message', {
                silent,
                reason: !mergedHasPreview ? 'merged-empty' : 'previous-fresher-or-equal',
                chat: summarizeChatLastMessageFieldsForLog(chat),
                previous: summarizeLastMessageForLog(previous.last_message),
                merged: summarizeLastMessageForLog(merged.last_message),
                matrix: summarizeLastMessageForLog(matrixChat?.last_message),
                previousTime,
                fetchedTime,
              })
            }

            return {
              ...merged,
              last_message: previous.last_message,
              last_message_id: previous.last_message_id ?? previous.last_message.id,
            }
          }

          if (merged.last_message && previous.last_message) {
            lastMessageDebugLog('fetchChats · use merged last_message', {
              silent,
              chat: summarizeChatLastMessageFieldsForLog(chat),
              merged: summarizeLastMessageForLog(merged.last_message),
              previous: summarizeLastMessageForLog(previous.last_message),
              matrix: summarizeLastMessageForLog(matrixChatsByRoomId.get(chat.matrix_room_id)?.last_message),
              previousTime,
              fetchedTime,
            })
          }

          return merged
        })
        .map(chat => {
          // Пока Matrix не подтвердил receipt, не возвращаем бейдж из room unread.
          if (chat.matrix_room_id && isTopicRoomLocallyRead(chat.matrix_room_id)) {
            return { ...chat, unread_count: 0 }
          }
          return chat
        })

      const accessibleChats = mergedChats.filter(chat => {
        // The server catalog controls visibility; SDK membership may still lag
        // behind invite/provisioning, so keep registered groups and task chats.
        if (isGroupLikeChatType(chat.type) || isEventChatItem(chat)) {
          return backendCatalogChatIds.has(chat.id)
            || Boolean(chat.matrix_room_id && backendCatalogRoomIds.has(chat.matrix_room_id))
        }

        if (!isChatAccessibleInMatrix(chat)) {
          logEventChat('fetch · filtered (no matrix access)', chat, {
            matrix: describeEventChatMatrixRoom(chat.matrix_room_id),
          })
          return false
        }

        return true
      })

      if (
        this.currentChat
        && !accessibleChats.some(chat => sameChatId(chat.id, this.currentChat!.id))
      ) {
        dropRoomMessages(this, this.activeTopic?.matrix_room_id ?? this.currentChat.matrix_room_id)
        this.currentChat = null
        clearMessages(this)
        this.activeTopic = null
        this.selectedTopicsGroupId = null
        this.showTopicsList = false
      }

      const resolvedChats = await this.resolveDialogTitlesWithEmployees(accessibleChats)
      if (isStale()) {
        return
      }
      this.chats = resolvedChats

      const dialogs = this.chats.filter(chat => chat.type === 'dialog' || chat.type === 'direct')
      chatDebugLog(`dialog titles · ${summarizeChatTitlesHeadline(this.chats)}`, {
        dialogs: (dialogs.length ? dialogs : this.chats)
          .slice(0, 12)
          .map(summarizeDialogTitleSourcesForLog),
      })

      for (const chat of this.chats) {
        cacheEventListPreview(chat)
      }
      syncChatListPreviewPending(this.chats, isEventsTab)

      for (const chat of accessibleChats) {
        if (!isGroupLikeChatType(chat.type) && !isEventChatItem(chat)) {
          continue
        }
        for (const roomId of collectBackendCatalogRoomIds([chat])) {
          if (isForgottenRoom(roomId)) {
            restoreForgottenRoom(roomId)
          }
        }
      }

      chatDebugLog('fetchChats → final chats set', {
        chatsCount: this.chats.length,
        chats: summarizeChatsForLog(this.chats, this.chats.length),
        previews: this.chats.slice(0, 8).map(chat => summarizeChatLastMessageFieldsForLog(chat)),
      })
      if (isEventsTab) {
        logEventChats('store · after fetch', this.chats)
      }

      if (!silent) {
        chatPerfLog('fetchChats · list ready (до превью)', {
          chatsCount: this.chats.length,
          afterProvisional: provisionalPublished,
        })
      }

      if (provisionalPublished) {
        notifyViewForceSortAndSync()
      }

      if (!isEventsTab) {
        persistChatListSnapshot(this.chats)
      }

      // Превью и presence догружаются в фоне: список показываем сразу,
      // pending-превью не сортируются до готовности (см. hydrateEventChatListPreviews).
      const hydratePromise = this.hydrateEventChatListPreviews()
      void hydratePromise
        .then(() => {
          if (!isEventsTab) {
            persistChatListSnapshot(this.chats)
          }
        })
        .catch(() => {})

      if (!silent) {
        void this.refreshMatrixPresence().catch(() => {})
        void hydratePromise
          .then(() => {
            chatPerfLog('fetchChats · ALL chat info ready (первый экран превью)', {
              chatsCount: this.chats.length,
              withPreview: this.chats.filter(chat => hasLastMessagePreview(chat.last_message)).length,
            })
          })
          .catch(() => {
            chatPerfLog('fetchChats · ALL chat info ready (hydrate error)', {
              chatsCount: this.chats.length,
            })
          })
      }

      if (matrixChats.length > 0) {
        this.syncTopicsUnreadForAllGroups()
        this.syncMatrixUnreadForAllChats()
      }

      this.updateTitleUnreadCount()

      if (silent) {
        const previousChatIds = new Set(previousChats.map(chat => String(chat.id)))
        const hasNewChats = accessibleChats.some(
          chat => !previousChatIds.has(String(chat.id)),
        )
        const viewIds = getViewChatContactIds()
        const viewMissingCoreChat = viewIds
          ? accessibleChats.some(chat => !viewIds.has(String(chat.id)))
          : false

        if (hasPendingChatListPreviews()) {
          syncViewWithCore()
        } else if (
          hasNewChats
          || accessibleChats.length !== previousChats.length
          || viewMissingCoreChat
        ) {
          notifyViewForceSortAndSync()
        } else if (hasSidebarListChanges(previousChats, accessibleChats)) {
          syncViewWithCore()
        }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      logger.error('chat_fetchChats_error', {
        file: 'chatStore',
        function: 'fetchChats',
        condition: message,
      })

      // НЕ выбрасываем ошибку дальше, чтобы не ломать приложение
    } finally {
      if (!isStale()) {
        fb.isGlobalChatLoading = false
      }
    }
  },

  /**
   * Превью сайдбара: REST часто без last_message, Matrix room — cold/forgotten.
   * Event-чаты: soft wait + retry-structure. Остальные — только history, без repair-шторма.
   */
  async hydrateEventChatListPreviews(options?: { limit?: number }): Promise<void> {
    let restoredAny = false
    const restoredChats = this.chats.map(chat => {
      if (hasLastMessagePreview(chat.last_message)) {
        cacheEventListPreview(chat)
        return chat
      }

      const restored = restoreEventListPreviewFromCache(chat)
      if (!restored) {
        return chat
      }

      restoredAny = true
      return restored
    })

    if (restoredAny) {
      this.chats = restoredChats
    }

    const isEventsTab = this.currentChatListTypes === CHAT_LIST_TYPES[CHAT_LIST_TAB.EVENTS]

    // Пустые (кэш / cooldown) — сразу «Нет сообщений», без вечного скелетона.
    for (const chat of this.chats) {
      const matchesTab = isEventsTab ? isEventChatItem(chat) : !isEventChatItem(chat)
      if (
        matchesTab
        && Boolean(chat.matrix_room_id?.trim())
        && !hasLastMessagePreview(chat.last_message)
        && shouldSkipEventListPreviewHydrate(chat.id)
      ) {
        settleChatListPreviewEmpty(chat.id)
      }
    }

    syncChatListPreviewPending(this.chats, isEventsTab)
    const allTargets = this.chats.filter(chat => {
      const matchesTab = isEventsTab ? isEventChatItem(chat) : !isEventChatItem(chat)
      return matchesTab
        && Boolean(chat.matrix_room_id?.trim())
        && !hasLastMessagePreview(chat.last_message)
        && !shouldSkipEventListPreviewHydrate(chat.id)
    })

    // Сначала только верх списка (видимый экран); остальное — hydrateMore / скролл.
    const HYDRATE_PREVIEW_CONCURRENCY = 4
    const HYDRATE_PREVIEW_INITIAL = options?.limit ?? 12
    const targets = allTargets.slice(0, HYDRATE_PREVIEW_INITIAL)

    if (!targets.length) {
      syncChatListPreviewPending(this.chats, isEventsTab)
      if (!getViewChatContactIds()) {
        notifyViewForceSortAndSync()
      }
      return
    }

    for (const chat of targets) {
      markChatListPreviewPending(chat.id)
    }

    let nextIndex = 0
    const worker = async () => {
      while (nextIndex < targets.length) {
        const chat = targets[nextIndex]
        nextIndex += 1
        await this.hydrateEventChatListPreview(chat)
      }
    }
    await Promise.all(
      Array.from({ length: Math.min(HYDRATE_PREVIEW_CONCURRENCY, targets.length) }, () => worker()),
    )
    // Не forceSort после каждого батча — иначе список прыгает, пока превью догружаются.
    // Полная сортировка один раз, когда pending больше нет.
    if (hasPendingChatListPreviews()) {
      syncViewWithCore()
    } else {
      notifyViewForceSortAndSync()
    }
  },

  /** Догрузка превью при скролле списка (следующие без last_message). */
  async hydrateMoreChatListPreviews(limit = 12): Promise<void> {
    await this.hydrateEventChatListPreviews({ limit })
  },

  async hydrateEventChatListPreview(chat: IChat): Promise<void> {
    const roomId = chat.matrix_room_id?.trim()
    if (!roomId) {
      return
    }

    if (this.currentChat && sameChatId(this.currentChat.id, chat.id)) {
      settleChatListPreviewReady(chat.id)
      return
    }

    const key = String(chat.id)
    const existing = eventListPreviewHydrateInFlight.get(key)
    if (existing) {
      return existing
    }

    const repairMembership = isEventChatItem(chat)

    const promise = (async () => {
      try {
        const liveChat = this.chats.find(item => sameChatId(item.id, chat.id))
        if (hasLastMessagePreview(liveChat?.last_message)) {
          if (liveChat) {
            cacheEventListPreview(liveChat)
          }
          settleChatListPreviewReady(chat.id)
          return
        }

        const cached = restoreEventListPreviewFromCache(chat)
        if (cached && hasLastMessagePreview(cached.last_message)) {
          const chatIndex = this.chats.findIndex(item => sameChatId(item.id, chat.id))
          if (chatIndex !== -1) {
            const nextChats = [...this.chats]
            nextChats[chatIndex] = {
              ...nextChats[chatIndex],
              ...pickFresherLastMessage(nextChats[chatIndex], cached),
            }
            this.chats = nextChats
            settleChatListPreviewReady(chat.id)
            if (cached.last_message) {
              notifyViewLastMessage(chat.id, cached.last_message, { bump: false })
            }
          }
          return
        }

        if (shouldSkipEventListPreviewHydrate(chat.id)) {
          settleChatListPreviewEmpty(chat.id)
          return
        }

        restoreForgottenRoom(roomId)

        const historyBlocked = isMatrixRoomHistoryForbidden(roomId)
        let joined = false

        if (!historyBlocked) {
          try {
            joined = await ensureMatrixChatMembership({
              chatId: chat.id,
              roomId,
              chat,
              isEventChat: repairMembership,
              softOnly: true,
            })
          } catch {
            // full membership repair attempted below for event chats
          }
        }

        if (repairMembership && !joined) {
          try {
            joined = await ensureMatrixChatMembership({
              chatId: chat.id,
              roomId,
              chat,
              isEventChat: true,
              forceRepair: true,
            })
          } catch {
            // preview still attempted below
          }
        }

        if (hasLastMessagePreview(
          this.chats.find(item => sameChatId(item.id, chat.id))?.last_message,
        )) {
          settleChatListPreviewReady(chat.id)
          return
        }

        clearMatrixRoomHistoryForbidden(roomId)
        let preview = await fetchMatrixRoomLastMessagePreview(roomId)

        // Уже join, но сообщений нет — повторный retry-structure не поможет и разгоняет шторм.
        if (repairMembership && !joined && !hasMatrixMessagePreview(preview)) {
          try {
            await ensureMatrixChatMembership({
              chatId: chat.id,
              roomId,
              chat,
              isEventChat: true,
              forceRepair: true,
            })
          } catch {
            // one more fetch attempt below
          }
          clearMatrixRoomHistoryForbidden(roomId)
          preview = await fetchMatrixRoomLastMessagePreview(roomId)
        }

        if (!hasMatrixMessagePreview(preview) || !preview) {
          logEventChat('list · preview empty', chat, {
            joined,
            historyBlocked: isMatrixRoomHistoryForbidden(roomId),
            matrix: describeEventChatMatrixRoom(roomId),
          })
          settleChatListPreviewEmpty(chat.id)
          markEventListPreviewHydrateAttempted(chat.id)
          return
        }

        const chatIndex = this.chats.findIndex(item => sameChatId(item.id, chat.id))
        if (chatIndex === -1) {
          settleChatListPreviewReady(chat.id)
          return
        }

        const current = this.chats[chatIndex]
        if (hasLastMessagePreview(current.last_message)) {
          const fresher = pickFresherLastMessage(current, {
            ...current,
            last_message: preview,
            last_message_id: preview.id,
          })
          if (!hasLastMessagePreview(fresher.last_message)
            || fresher.last_message?.id === current.last_message?.id) {
            settleChatListPreviewReady(chat.id)
            return
          }
        }

        const updatedChat: IChat = {
          ...current,
          last_message: preview,
          last_message_id: preview.id,
        }
        const nextChats = [...this.chats]
        nextChats[chatIndex] = updatedChat
        this.chats = nextChats

        if (this.currentChat && sameChatId(this.currentChat.id, chat.id)) {
          this.currentChat = {
            ...this.currentChat,
            last_message: preview,
            last_message_id: preview.id,
          }
        }

        logEventChat('list · preview hydrated', updatedChat, {
          preview: summarizeLastMessageForLog(preview),
        })
        cacheEventListPreview(updatedChat)
        settleChatListPreviewReady(chat.id)
        notifyViewLastMessage(chat.id, preview, { bump: false })
      } catch (error) {
        logEventChat('list · preview hydrate failed', chat, {
          error: error instanceof Error ? error.message : String(error),
          matrix: describeEventChatMatrixRoom(roomId),
        })
        if (!hasLastMessagePreview(
          this.chats.find(item => sameChatId(item.id, chat.id))?.last_message,
        )) {
          settleChatListPreviewEmpty(chat.id)
          markEventListPreviewHydrateAttempted(chat.id)
        }
      } finally {
        eventListPreviewHydrateInFlight.delete(key)
      }
    })()

    eventListPreviewHydrateInFlight.set(key, promise)
    return promise
  },

  // Создаёт группу/канал через backend; Matrix room провижинится асинхронно
  async createChat(payload: {
    type: 'group' | 'channel'
    title: string
    description?: string
    icon?: File | null
    inviteUserIds?: Array<string | number>
    enableTopics?: boolean
    topicsDisplayMode?: 'list' | 'tabs'
  }): Promise<IChat> {
    if (!checkAuth()) {
      throw new Error('Пользователь не авторизован')
    }

    try {
      const inviteUserIds = payload.inviteUserIds?.length
        ? filterValidUserIds(payload.inviteUserIds).map(String)
        : []

      const body = {
        title: payload.title,
        description: payload.description || '',
        members_ids: inviteUserIds,
        discussion_mode: payload.enableTopics ? 'topics' as const : undefined,
        topics_display_mode: payload.enableTopics
          ? (payload.topicsDisplayMode ?? 'list')
          : undefined,
      }

      let chat = payload.type === 'channel'
        ? await ChatApiService.createChannel(body)
        : await ChatApiService.createGroup(body)

      if (!chat.matrix_room_id) {
        chat = await pollChatUntilMatrixRoom(
          () => ChatApiService.fetchChatById(chat.id),
          { attempts: 12, delayMs: 1500 },
        )
      }

      if (payload.icon instanceof File) {
        try {
          chat = await ChatApiService.uploadAvatar(chat.id, payload.icon)
        } catch {
          // Аватар не критичен для создания чата
        }
      }

      if (payload.enableTopics && chat.discussion_mode !== 'topics') {
        try {
          chat = await ChatApiService.enableTopics(chat.id)
        } catch (error) {
          logger.error('chat_enableTopics_on_create_error', {
            file: 'chatStore',
            function: 'createChat',
            condition: String(error),
          })
        }
      }

      this.chats.unshift(chat)

      useFeedbackStore().showToast({
        type: 'success',
        message: 'Чат успешно создан',
        time: 3000,
      })

      return chat
    } catch (error) {
      logger.error('chat_createChat_error', {
        file: 'chatStore',
        function: 'createChat',
        condition: String(error),
      })
      useFeedbackStore().showToast({
        type: 'error',
        message: 'Не удалось создать чат',
        time: 7000,
      })
      throw error
    }
  },

  async fetchChat(chatId: number, options?: { force?: boolean }): Promise<IChat> {
    if (!checkAuth()) {
      throw new Error('Пользователь не авторизован')
    }

    if (!options?.force) {
      const fetchedAt = fetchChatFetchedAt.get(chatId) ?? 0
      if (Date.now() - fetchedAt < FETCH_CHAT_COOLDOWN_MS) {
        const cached = this.chats.find(c => c.id === chatId)
            ?? (this.currentChat?.id === chatId ? this.currentChat : undefined)
        if (cached?.matrix_room_id?.trim()) {
          return cached
        }
      }
    }

    const inflight = fetchChatInFlight.get(chatId)
    if (inflight) {
      return inflight
    }

    const promise = (async (): Promise<IChat> => {
      try {
        const existing = this.chats.find(c => c.id === chatId)
            ?? (this.currentChat?.id === chatId ? this.currentChat : undefined)

        let chat: IChat | null = null
        // Hash(roomId) без Django PK — GET /chat/{id}/ даст 403, берём resolve/?room_id=
        const skipBackendById = isSyntheticMatrixChatId(chatId, {
          matrix_room_id: existing?.matrix_room_id,
        })

        const mergeBackendWithMatrix = async (backendChat: IChat): Promise<IChat> => {
          try {
            const matrixChat = await fetchChatFromMatrix(backendChat.id, { ...existing, ...backendChat })
            // Не спред matrix поверх backend — иначе title/last_message прыгают на Matrix-заглушки.
            let merged = mergeBackendChatWithMatrix(backendChat, matrixChat)
            merged = {
              ...merged,
              matrix_room_id: backendChat.matrix_room_id?.trim()
                  || matrixChat.matrix_room_id?.trim()
                  || existing?.matrix_room_id?.trim()
                  || undefined,
              discussion_mode: backendChat.discussion_mode
                  ?? existing?.discussion_mode
                  ?? matrixChat.discussion_mode,
              topics: backendChat.topics?.length
                ? backendChat.topics
                : (existing?.topics?.length ? existing.topics : (matrixChat.topics ?? backendChat.topics)),
              capabilities: backendChat.capabilities ?? existing?.capabilities ?? matrixChat.capabilities,
              space_room_id: backendChat.space_room_id ?? existing?.space_room_id ?? matrixChat.space_room_id,
              parent_space_id: backendChat.parent_space_id,
              icon: resolveChatIcon(
                backendChat.icon,
                backendChat.display_avatar_url,
                backendChat.avatar_url,
                backendChat.dialog_member?.avatar,
                existing?.icon,
                matrixChat.icon,
              ),
              dialog_member: backendChat.dialog_member ?? existing?.dialog_member ?? matrixChat.dialog_member,
              display_avatar_url: backendChat.display_avatar_url
                  ?? existing?.display_avatar_url
                  ?? matrixChat.display_avatar_url,
            }

            if (existing?.last_message) {
              merged = {
                ...merged,
                ...pickFresherLastMessage(existing, merged),
              }
            }

            return merged
          } catch {
            if (existing?.last_message) {
              return {
                ...backendChat,
                ...pickFresherLastMessage(existing, backendChat),
              }
            }
            return backendChat
          }
        }

        if (!skipBackendById) {
          try {
            chat = await ChatApiService.fetchChatById(chatId)
          } catch {
            // Чат может существовать только в Matrix
          }
        } else {
          let matrixOnlyChat = existing
          if (!matrixOnlyChat?.matrix_peer_id) {
            try {
              matrixOnlyChat = await fetchChatFromMatrix(chatId, existing)
            } catch {
            }
          }

          const registered = matrixOnlyChat
            ? await this.registerMatrixOnlyDialog(matrixOnlyChat)
            : null
          if (registered) {
            chat = registered
          } else if (matrixOnlyChat?.matrix_room_id) {
            try {
              chat = await ChatApiService.resolveRoom(matrixOnlyChat.matrix_room_id)
            } catch {
              // Комната ещё не заведена в backend
            }
          }
        }

        if (chat) {
          chat = await mergeBackendWithMatrix(chat)

          if (chat.transition_status === 'failed') {
            void requestStructureRepair(chat.id)
          }
        } else {
          chat = await fetchChatFromMatrix(chatId, existing)
        }

        if (!chat.matrix_room_id?.trim() && existing?.matrix_room_id?.trim()) {
          chat = { ...chat, matrix_room_id: existing.matrix_room_id.trim() }
        }

        chat = {
          ...chat,
          total_members: preserveTotalMembers(chat.total_members, existing?.total_members),
        }
        this.upsertChatInList(chat)
        if (chat.id !== chatId) {
          this.chats = this.chats.filter(c => c.id !== chatId)
        }

        if (this.currentChat?.id === chatId || this.currentChat?.id === chat.id) {
          this.setCurrentChatPreservingMemberCount(chat)
        }

        fetchChatFetchedAt.set(chatId, Date.now())

        return chat
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error)
        if (!message.includes('Chat room not found for chat')) {
          logger.error('chat_fetchChat_error', {
            file: 'chatStore',
            function: 'fetchChat',
            condition: message,
          })
        }
        throw error
      }
    })()

    fetchChatInFlight.set(chatId, promise)
    try {
      return await promise
    } finally {
      if (fetchChatInFlight.get(chatId) === promise) {
        fetchChatInFlight.delete(chatId)
      }
    }
  },

  async updateChat(
    chatId: number,
    payload: Partial<IChat> & { icon?: File | string | null },
  ): Promise<IChat> {
    if (!checkAuth()) {
      throw new Error('Пользователь не авторизован')
    }

    try {
      const chat = this.chats.find(c => c.id === chatId)
          ?? (this.currentChat?.id === chatId ? this.currentChat : undefined)

      if (isSyntheticMatrixChatId(chatId, { matrix_room_id: chat?.matrix_room_id })) {
        throw new Error('Чат ещё не зарегистрирован в backend')
      }

      const previousIcon = resolveChatIcon(
        chat?.icon,
        chat?.display_avatar_url,
        chat?.avatar_url,
      )
      let updatedChat = chat as IChat

      if (
        payload.title !== undefined
          || payload.description !== undefined
          || payload.topics_display_mode !== undefined
      ) {
        updatedChat = await ChatApiService.updateChatSettings(chatId, {
          ...(payload.title !== undefined ? { title: payload.title } : {}),
          ...(payload.description !== undefined ? { description: payload.description } : {}),
          ...(payload.topics_display_mode !== undefined
            ? { topics_display_mode: payload.topics_display_mode }
            : {}),
        })
      }

      if (payload.icon instanceof File) {
        updatedChat = await ChatApiService.uploadAvatar(chatId, payload.icon)
      } else if (payload.icon === null) {
        updatedChat = await ChatApiService.deleteAvatar(chatId)
      }

      // Settings PATCH / отсутствие icon в payload — не затираем уже показанный icon.
      if (payload.icon === undefined) {
        updatedChat = {
          ...updatedChat,
          icon: resolveChatIcon(
            updatedChat.icon,
            updatedChat.display_avatar_url,
            updatedChat.avatar_url,
            previousIcon,
          ),
        }
      }

      const chatIndex = this.chats.findIndex(c => c.id === chatId)
      if (chatIndex !== -1) {
        const previous = this.chats[chatIndex]
        this.chats.splice(chatIndex, 1, {
          ...previous,
          ...updatedChat,
          total_members: preserveTotalMembers(previous.total_members, updatedChat.total_members),
          icon: resolveChatIcon(
            updatedChat.icon,
            updatedChat.display_avatar_url,
            updatedChat.avatar_url,
            previous.icon,
          ),
        })
      }

      if (this.currentChat?.id === chatId) {
        this.currentChat = {
          ...this.currentChat,
          ...updatedChat,
          total_members: preserveTotalMembers(
            this.currentChat.total_members,
            updatedChat.total_members,
          ),
          icon: resolveChatIcon(
            updatedChat.icon,
            updatedChat.display_avatar_url,
            updatedChat.avatar_url,
            this.currentChat.icon,
          ),
        }
      }

      syncViewWithCore()

      useFeedbackStore().showToast({
        type: 'success',
        message: 'Чат успешно обновлен',
        time: 3000,
      })

      return updatedChat
    } catch (error) {
      logger.error('chat_updateChat_error', {
        file: 'chatStore',
        function: 'updateChat',
        condition: String(error),
      })
      useFeedbackStore().showToast({
        type: 'error',
        message: 'Не удалось обновить чат',
        time: 7000,
      })
      throw error
    }
  },

  async setChatPinned(chatId: number, pinned: boolean): Promise<IChat> {
    if (!checkAuth()) {
      throw new Error('Пользователь не авторизован')
    }

    const chat = this.chats.find(c => c.id === chatId)
      ?? (this.currentChat?.id === chatId ? this.currentChat : undefined)

    if (isSyntheticMatrixChatId(chatId, { matrix_room_id: chat?.matrix_room_id })) {
      throw new Error('Чат ещё не зарегистрирован в backend')
    }

    try {
      const updatedChat = pinned
        ? await ChatApiService.pinChat(chatId)
        : await ChatApiService.unpinChat(chatId)

      this.upsertChatInList(updatedChat)

      if (this.currentChat?.id === chatId) {
        this.setCurrentChatPreservingMemberCount({
          ...this.currentChat,
          ...updatedChat,
          is_pinned: updatedChat.is_pinned,
          pinned_at: updatedChat.pinned_at ?? null,
        })
      }

      notifyViewForceSortAndSync()

      return updatedChat
    } catch (error) {
      logger.error('chat_setChatPinned_error', {
        file: 'chatStore',
        function: 'setChatPinned',
        condition: String(error),
      })
      useFeedbackStore().showToast({
        type: 'error',
        message: pinned ? 'Не удалось закрепить чат' : 'Не удалось открепить чат',
        time: 7000,
      })
      throw error
    }
  },

  // Удаляет участника из чата напрямую через Matrix (kick)
  async removeMemberFromChat(
    chatId: number,
    memberId: string,
  ): Promise<void> {
    if (!checkAuth()) {
      throw new Error('Пользователь не авторизован')
    }

    try {
      const chat = this.chats.find(c => c.id === chatId)
          ?? (this.currentChat?.id === chatId ? this.currentChat : undefined)
      const roomId = chat?.matrix_room_id ?? await resolveMatrixRoomIdByChatId(chatId)

      if (!roomId) {
        throw new Error('Чат не найден')
      }

      const chatMeta = this.chats.find(c => c.id === chatId)
          ?? (this.currentChat?.id === chatId ? this.currentChat : undefined)

      if (chatMeta && !isSyntheticMatrixChatId(chatId, { matrix_room_id: chatMeta.matrix_room_id })) {
        await ChatApiService.removeMember(chatId, memberId)
      } else {
        await kickMatrixRoomMember(roomId, memberId)
      }

      await this.refreshChatMemberCount(chatId)

      useFeedbackStore().showToast({
        type: 'success',
        message: 'Участник удален из чата',
        time: 3000,
      })
    } catch (error) {
      logger.error('chat_removeMember_error', {
        file: 'chatStore',
        function: 'removeMemberFromChat',
        condition: String(error),
      })
      useFeedbackStore().showToast({
        type: 'error',
        message: 'Не удалось удалить участника',
        time: 7000,
      })
      throw error
    }
  },

  // Добавляет участников: backend add-members (с fan-out на темы), иначе Matrix invite
  async addMembersToGroup(
    chatId: number,
    userIds: Array<string | number>,
  ): Promise<{ invited: number; unresolved: Array<string | number> }> {
    if (!checkAuth()) {
      throw new Error('Пользователь не авторизован')
    }

    const chat = this.chats.find(c => c.id === chatId)
        ?? (this.currentChat?.id === chatId ? this.currentChat : undefined)
    const validUserIds = filterValidUserIds(userIds).map(String)

    if (!validUserIds.length) {
      return { invited: 0, unresolved: [] }
    }

    if (chat && !isSyntheticMatrixChatId(chatId, { matrix_room_id: chat.matrix_room_id })) {
      await ChatApiService.addMembers(chatId, validUserIds)
      void this.refreshChatMemberCount(chatId)
      return { invited: validUserIds.length, unresolved: [] }
    }

    throw new Error('Чат не найден или ещё не зарегистрирован в backend')
  },

})
